// The keyboard as a runtime Program (runtime/effects.ts): the parser's
// state, what it is waiting for, and the short notice shown when a command
// has nothing to act on, changed only by messages. Keys, time passing and
// a new bindings table all arrive as messages; the commands a key names
// leave as a `port` effect, which the router offers to the scopes.

import { after, cancel, port, type Effect, type Next } from '../runtime/effects';
import { compile, type Bindings } from './bindings';
import { phrase } from '../commands/words';
import { keyLabel, type Chord } from './chord';
import { IDLE, PENDING_MS, step, type Context, type Match, type ParserState, type Pending, type Rejected } from './parser';

/** How long a notice stays, in milliseconds. */
export const NOTICE_MS = 4000;

/** Something said to the person about a key: shown small, and read out. */
export interface Notice {
  /** Counts notices, so the same words said twice are two notices. */
  id: number;
  text: string;
}

export interface KeysModel {
  bindings: Bindings;
  parser: ParserState;
  /** What is typed so far and what may follow; null when nothing is pending. */
  pending: Pending | null;
  notice: Notice | null;
  notices: number;
}

export type KeysMsg =
  | { kind: 'key'; chord: Chord; at: number; context: Context }
  /** Time passed: it is now `at`. */
  | { kind: 'elapsed'; at: number }
  /** The person turned to something else: nothing stays pending. */
  | { kind: 'reset' }
  /** Use this table from now on (a remap). */
  | { kind: 'bindings'; bindings: Bindings }
  | { kind: 'notice'; text: string }
  | { kind: 'notice-over'; id: number };

/** What a key came to: the commands it named for the scopes, and whether the parser used the key. */
export interface Offer {
  matches: readonly Match[];
  taken: boolean;
}

type Fx = Effect<KeysMsg, Offer>[];

/** What a refused input is said as: the keys typed, and why they did nothing. */
function rejection(r: Rejected): string {
  const typed = `${r.count ?? ''}${r.keys.map(keyLabel).join('')}`;
  return r.why === 'no-count' && r.command ? `${typed}: ${phrase(r.command)} takes no count` : `${typed}: no such key`;
}

const NOTHING_LIVE: Context = { sections: [], typing: false, onControl: false };

export function initKeys(bindings: Bindings): Next<KeysModel, KeysMsg, Offer> {
  return [{ bindings, parser: IDLE, pending: null, notice: null, notices: 0 }];
}

export function updateKeys(m: KeysModel, msg: KeysMsg): Next<KeysModel, KeysMsg, Offer> {
  switch (msg.kind) {
    case 'key': {
      const s = step(m.parser, { kind: 'key', chord: msg.chord, at: msg.at }, compile(m.bindings), msg.context);
      const fx: Fx = [s.state.kind === 'idle' ? cancel('pending') : after('pending', Math.max(0, s.state.at + PENDING_MS - msg.at), { kind: 'elapsed', at: s.state.at + PENDING_MS })];
      let next: KeysModel = { ...m, parser: s.state, pending: s.pending };
      if (s.rejected) {
        // Nothing typed is dropped in silence: what was refused is said.
        const id = m.notices + 1;
        next = { ...next, notice: { id, text: rejection(s.rejected) }, notices: id };
        fx.push(after('notice', NOTICE_MS, { kind: 'notice-over', id }));
      } else if (s.taken && s.state.kind !== 'rejected') {
        // A key the parser used ends the last notice: what it said was about the key before.
        next = { ...next, notice: null };
      }
      return [next, [...fx, port({ matches: s.matches, taken: s.taken })]];
    }
    case 'elapsed': {
      const s = step(m.parser, msg, compile(m.bindings), NOTHING_LIVE);
      return s.state === m.parser ? [m] : [{ ...m, parser: s.state, pending: null }];
    }
    case 'reset':
      return m.parser.kind === 'idle' ? [m] : [{ ...m, parser: IDLE, pending: null }, [cancel('pending')]];
    case 'bindings':
      return [{ ...m, bindings: msg.bindings, parser: IDLE, pending: null }, [cancel('pending')]];
    case 'notice': {
      const id = m.notices + 1;
      return [{ ...m, notice: { id, text: msg.text }, notices: id }, [after('notice', NOTICE_MS, { kind: 'notice-over', id })]];
    }
    case 'notice-over':
      return m.notice?.id === msg.id ? [{ ...m, notice: null }] : [m];
  }
}
